import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Youtube, Trash2, Info, Plus, Link2, ListVideo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, useQ, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { PageHeader, Panel, Empty } from "@/components/common";

const ORIGIN = window.location.origin;

const ConnectYouTube = () => {
  const { t } = useT();
  const api = useApi();
  const { data: status, error, refetch } = useQ(`/youtube/status?origin=${encodeURIComponent(ORIGIN)}`);
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/youtube/auth-url", { params: { origin: ORIGIN }, withCredentials: true });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (error) return <Panel role="alert" data-testid="youtube-connect-load-error">{errMsg(error)}<Button onClick={() => refetch()} data-testid="youtube-connect-retry">{t("retry")}</Button></Panel>;
  if (!status) return <Panel data-testid="youtube-connect-loading">{t("loading")}</Panel>;
  return (
    <Panel className="mb-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="yt-connect-panel">
      <div className="h-12 w-12 rounded-full bg-rose-600/20 flex items-center justify-center shrink-0"><Youtube className="h-6 w-6 text-rose-400" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg font-bold">{t("yt_real_title")}</p>
        {status.configured ? (
          <p className="text-sm text-slate-400">{t("yt_real_desc")}</p>
        ) : (
          <p className="text-sm text-amber-200/90" data-testid="yt-not-configured">{t("yt_not_configured")}</p>
        )}
        <p className="text-xs text-slate-500 mt-2 break-all">{t("yt_redirect")}: <span className="font-mono-ui text-slate-300" data-testid="yt-redirect-uri">{status.redirect_uri}</span></p>
      </div>
      <Button onClick={connect} disabled={!status.configured || busy} className="rounded-full bg-rose-600 hover:bg-rose-500 shrink-0" data-testid="yt-connect-btn">
        <Link2 className="h-4 w-4 mr-1" />{t("yt_connect")}
      </Button>
    </Panel>
  );
};

const Broadcasts = ({ channel }) => {
  const { t } = useT();
  const { data = [], isLoading, error } = useQ(`/channels/${channel.id}/broadcasts`);
  if (isLoading) return <p className="text-xs text-slate-500 mt-3">…</p>;
  if (error) return <p className="text-xs text-rose-300 mt-3">{errMsg(error)}</p>;
  return (
    <ul className="mt-3 space-y-1 text-sm" data-testid={`broadcasts-${channel.id}`}>
      {data.length === 0 && <li className="text-slate-500 text-xs">{t("no_broadcasts")}</li>}
      {data.map((b) => (
        <li key={b.id} className="flex justify-between gap-3 rounded bg-white/[0.03] px-3 py-2">
          <span className="truncate">{b.title}</span>
          <span className="text-xs text-slate-400 uppercase shrink-0">{b.status}</span>
        </li>
      ))}
    </ul>
  );
};

const ChannelRow = ({ c, onRemove }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const real = c.connection === "youtube";
  return (
    <Panel className="!p-4" data-testid={`channel-row-${c.id}`}>
      <div className="flex items-center gap-4">
        {c.thumbnail ? <img src={c.thumbnail} alt="" className="h-10 w-10 rounded-full" /> :
          <div className="h-10 w-10 rounded-full bg-rose-600/20 flex items-center justify-center"><Youtube className="h-5 w-5 text-rose-400" /></div>}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{c.name}</p>
          <p className="text-xs text-slate-500">@{c.handle} · <span className={real ? "text-emerald-400" : "text-amber-300"} data-testid={`channel-type-${c.id}`}>{real ? t("yt_connected") : t("simulated")}</span></p>
        </div>
        {real && <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid={`channel-broadcasts-btn-${c.id}`}><ListVideo className="h-4 w-4" /></Button>}
        <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-400" data-testid={`channel-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {real && <Link to={`/dashboard/youtube?channel=${c.id}`} className="inline-flex items-center gap-2 mt-4 text-sm text-rose-300 hover:text-rose-200" data-testid={`channel-studio-${c.id}`}><Youtube className="h-4 w-4" />{t("studio_go_live")}</Link>}
      {open && <Broadcasts channel={c} />}
    </Panel>
  );
};

export default function Channels() {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const { data: channels = [] } = useQ("/channels");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [params, setParams] = useSearchParams();
  const refresh = () => qc.invalidateQueries({ queryKey: ["/channels"] });

  useEffect(() => {
    if (params.get("connected")) toast.success(t("yt_connected_toast"));
    if (params.get("yt_error")) toast.error(`${t("yt_error")}: ${params.get("yt_error")}`);
    if (params.get("connected") || params.get("yt_error")) setParams({}, { replace: true });
  }, [params, setParams, t]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/channels", { name, handle }); setName(""); setHandle(""); refresh(); toast.success(t("channel_added")); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/channels/${id}`); refresh(); } catch (err) { toast.error(errMsg(err)); } };

  return (
    <div>
      <PageHeader eyebrow={t("nav_channels")} title={t("channels_title")} subtitle={t("channels_sub")} />
      <ConnectYouTube />
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel>
          <p className="font-display text-lg font-bold mb-2">{t("sim_channel_title")}</p>
          <div className="flex gap-2 text-xs text-slate-400 mb-4" data-testid="channels-simulated-notice"><Info className="h-4 w-4 shrink-0" />{t("channels_notice")}</div>
          <form onSubmit={add} className="space-y-3">
            <Input required placeholder={t("channel_name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="channel-name-input" />
            <Input required placeholder="@handle" value={handle} onChange={(e) => setHandle(e.target.value)} data-testid="channel-handle-input" />
            <Button type="submit" variant="outline" className="w-full rounded-full" data-testid="channel-add-btn"><Plus className="h-4 w-4 mr-1" />{t("add_sim_channel")}</Button>
          </form>
        </Panel>
        <div className="lg:col-span-2 space-y-3">
          {channels.length === 0 ? <Empty icon={Youtube} title={t("no_channels")} testId="channels-empty" /> :
            channels.map((c) => <ChannelRow key={c.id} c={c} onRemove={() => remove(c.id)} />)}
        </div>
      </div>
    </div>
  );
}
